import { Field } from '@atlaskit/form';
import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import Select, {
  type FormatOptionLabelMeta,
  type StylesConfig
} from '@databases/atlaskit/Select';
import { messages as intlMessages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import {
  addChangeToDraft,
  type AddChangeToDraftOptions
} from '@databases/redux/changesets/actions';
import {
  type DatabaseField,
  type DatabaseFieldType,
  type NumberFormat,
  NumberFormats,
  NumberSeparators,
  predefinedNumberFormats
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

type NumberFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.NUMBER>;
};

type NumberFormatOption = {
  icon: string;
  label: string;
  value: NumberFormats.NUMBER | NumberFormats.PERCENT | NumberFormats.CURRENCY;
};

type DecimalPlacesOption = {
  example?: string;
  label: string;
  value: number | undefined;
};

type SeparatorValue = Pick<NumberFormat, 'decimalSeparator' | 'thousandsSeparator'>;

type SeparatorOption = {
  example: string;
  label: string;
  value: SeparatorValue;
};

type CurrencyOption = {
  label: string;
  value: string;
};

const formatButtonStyles = xcss({
  alignItems: 'center',
  backgroundColor: 'elevation.surface',
  borderColor: 'color.border',
  borderRadius: 'radius.medium',
  borderStyle: 'solid',
  borderWidth: 'border.width',
  color: 'color.text',
  cursor: 'pointer',
  display: 'flex',
  height: '60px',
  justifyContent: 'center',
  overflow: 'hidden',
  paddingBlock: 'space.150',
  paddingInline: 'space.100',
  whiteSpace: 'nowrap',
  width: '83px',
  ':hover': {
    backgroundColor: 'color.background.selected',
    borderColor: 'color.border.selected',
    color: 'color.text.selected'
  }
});

const selectedFormatButtonStyles = xcss({
  backgroundColor: 'color.background.selected',
  borderColor: 'color.border.selected',
  color: 'color.text.selected'
});

const decimalOptionStyles = xcss({
  width: '100%'
});

const inputWrapperStyles = xcss({
  maxWidth: '100%',
  width: '266px'
});

const decimalPlacesSelectStyles: StylesConfig<DecimalPlacesOption, false> = {
  ...getCompactSelectStyles<DecimalPlacesOption, false>(),
  menuPortal: (base) => ({
    ...base,
    zIndex: 750
  })
};
const separatorSelectStyles: StylesConfig<SeparatorOption, false> = {
  ...getCompactSelectStyles<SeparatorOption, false>(),
  menuPortal: (base) => ({
    ...base,
    zIndex: 750
  })
};
const currencySelectStyles: StylesConfig<CurrencyOption, false> = {
  ...getCompactSelectStyles<CurrencyOption, false>(),
  menuPortal: (base) => ({
    ...base,
    zIndex: 750
  })
};

const isSelectedSeparator = (
  option: SeparatorOption,
  numberFormat: NumberFormat
): boolean =>
  option.value.thousandsSeparator === numberFormat.thousandsSeparator &&
  option.value.decimalSeparator === numberFormat.decimalSeparator;

const getNumberFieldSettingsMenuPortalTarget = (): HTMLElement | undefined =>
  typeof globalThis.document === 'undefined' ? undefined : globalThis.document.body;

const NumberFieldSettings: FunctionComponent<NumberFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const formatLabel = formatMessage(intlMessages['field-settings.type-number-format']);
  const decimalPlacesLabel = formatMessage(intlMessages['number-field.decimal-places']);
  const separatorsLabel = formatMessage(
    intlMessages['number-field.thousands-and-decimal-separators']
  );
  const currencyLabel = formatMessage(intlMessages['field-settings.currency-type-label']);
  const menuPortalTarget = getNumberFieldSettingsMenuPortalTarget();
  const fieldSettingsAnalyticsOptions = useMemo(
    () =>
      ({
        fieldSettingsAnalytics: {
          source: 'fieldContextualMenu',
          fieldType: field.type
        }
      }) as AddChangeToDraftOptions,
    [field.type]
  );

  const formatOptions = useMemo<NumberFormatOption[]>(
    () => [
      {
        icon: '#',
        label: formatMessage(intlMessages['field-settings.type-number']),
        value: NumberFormats.NUMBER
      },
      {
        icon: '%',
        label: formatMessage(intlMessages['field-settings.type-number-format-percent']),
        value: NumberFormats.PERCENT
      },
      {
        icon: '$',
        label: formatMessage(intlMessages['field-settings.type-number-format-currency']),
        value: NumberFormats.CURRENCY
      }
    ],
    [formatMessage]
  );

  const decimalPlacesOptions = useMemo<DecimalPlacesOption[]>(
    () => [
      {
        label: formatMessage(intlMessages['number-field.decimal-places-manual']),
        value: undefined
      },
      {
        label: formatMessage(intlMessages['number-field.decimal-places-none']),
        value: 0
      },
      ...Array.from({ length: 5 }, (_, index) => {
        const decimalPlaces = index + 1;
        return {
          example: `1.${'0'.repeat(decimalPlaces)}`,
          label: String(decimalPlaces),
          value: decimalPlaces
        };
      })
    ],
    [formatMessage]
  );

  const separatorOptions = useMemo<SeparatorOption[]>(() => {
    const supportedOptions: SeparatorOption[] = [
      {
        example: '1000000',
        label: formatMessage(messages.numberSeparatorNoFormatting),
        value: {
          thousandsSeparator: undefined,
          decimalSeparator: NumberSeparators.DOT
        }
      },
      {
        example: '1,000,000.00',
        label: formatMessage(messages.numberSeparatorCommaPeriod),
        value: {
          thousandsSeparator: NumberSeparators.COMMA,
          decimalSeparator: NumberSeparators.DOT
        }
      },
      {
        example: '1.000.000,00',
        label: formatMessage(messages.numberSeparatorPeriodComma),
        value: {
          thousandsSeparator: NumberSeparators.DOT,
          decimalSeparator: NumberSeparators.COMMA
        }
      },
      {
        example: `1${NumberSeparators.WHITESPACE}000${NumberSeparators.WHITESPACE}000,00`,
        label: formatMessage(messages.numberSeparatorSpaceComma),
        value: {
          thousandsSeparator: NumberSeparators.WHITESPACE,
          decimalSeparator: NumberSeparators.COMMA
        }
      },
      {
        example: `1${NumberSeparators.WHITESPACE}000${NumberSeparators.WHITESPACE}000.00`,
        label: formatMessage(messages.numberSeparatorSpacePeriod),
        value: {
          thousandsSeparator: NumberSeparators.WHITESPACE,
          decimalSeparator: NumberSeparators.DOT
        }
      },
      {
        example: '1,000,000,00',
        label: formatMessage(messages.numberSeparatorCommaComma),
        value: {
          thousandsSeparator: NumberSeparators.COMMA,
          decimalSeparator: NumberSeparators.COMMA
        }
      },
      {
        example: '1.000.000.00',
        label: formatMessage(messages.numberSeparatorPeriodPeriod),
        value: {
          thousandsSeparator: NumberSeparators.DOT,
          decimalSeparator: NumberSeparators.DOT
        }
      }
    ];

    return supportedOptions;
  }, [formatMessage]);

  const currencyOptions = useMemo<CurrencyOption[]>(
    () => [
      {
        value: '$',
        label: formatMessage(intlMessages['field-settings.currency-label-dollar'])
      },
      {
        value: '€',
        label: formatMessage(intlMessages['field-settings.currency-label-euro'])
      },
      {
        value: '£',
        label: formatMessage(intlMessages['field-settings.currency-label-pound'])
      },
      {
        value: '¥',
        label: formatMessage(intlMessages['field-settings.currency-label-yen'])
      },
      {
        value: 'A$',
        label: formatMessage(
          intlMessages['field-settings.currency-label-australian-dollar']
        )
      }
    ],
    [formatMessage]
  );

  const handleFormatChange = useCallback(
    (format: NumberFormatOption['value']) => {
      const predefinedFormat = predefinedNumberFormats[format];
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_SETTINGS_NUMBER_FORMAT,
            fieldId: field.id,
            newValue: {
              ...field.settings.numberFormat,
              format,
              prefix: predefinedFormat.prefix,
              suffix: predefinedFormat.suffix
            }
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [
      contentId,
      dispatch,
      field.id,
      field.settings.numberFormat,
      fieldSettingsAnalyticsOptions
    ]
  );

  const handleDecimalPlacesChange = useCallback(
    (option: DecimalPlacesOption) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_SETTINGS_MINIMUM_OR_MAXIMUM_FRACTION_DIGITS_FIELD,
            fieldId: field.id,
            changeType: 'decimalPlaces',
            newValue: option.value
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [contentId, dispatch, field.id, fieldSettingsAnalyticsOptions]
  );

  const handleSeparatorChange = useCallback(
    (option: SeparatorOption) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_SETTINGS_THOUSANDS_AND_DECIMAL_FIELD,
            fieldId: field.id,
            newValue: option.value
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [contentId, dispatch, field.id, fieldSettingsAnalyticsOptions]
  );

  const handleCurrencyChange = useCallback(
    (option: CurrencyOption) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD,
            fieldId: field.id,
            changeType: 'prefix',
            newValue: option.value
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [contentId, dispatch, field.id, fieldSettingsAnalyticsOptions]
  );

  const formatOptionWithExample = useCallback(
    (option: { example?: string; label: string }) => (
      <Box xcss={decimalOptionStyles}>
        <Inline alignBlock="center" spread="space-between" shouldWrap={false}>
          {option.label}
          {option.example && <Text color="color.text.subtlest">{option.example}</Text>}
        </Inline>
      </Box>
    ),
    []
  );
  const formatDecimalPlacesOption = useCallback(
    (option: DecimalPlacesOption, meta: FormatOptionLabelMeta<DecimalPlacesOption>) =>
      meta.context === 'value' ? option.label : formatOptionWithExample(option),
    [formatOptionWithExample]
  );
  const formatSeparatorOption = useCallback(
    (option: SeparatorOption, meta: FormatOptionLabelMeta<SeparatorOption>) =>
      meta.context === 'value' ? option.label : formatOptionWithExample(option),
    [formatOptionWithExample]
  );

  return (
    <Box paddingBlockEnd="space.025">
      <Stack space="space.050">
        <Field
          label={
            <Text size="small" weight="bold" color="color.text.subtle">
              {formatLabel}
            </Text>
          }
          name="number-format-field"
        >
          {() => (
            <Inline alignBlock="center" space="space.100" shouldWrap>
              {formatOptions.map((option) => {
                const isSelected = option.value === field.settings.numberFormat.format;

                return (
                  <Pressable
                    aria-pressed={isSelected}
                    key={option.value}
                    onClick={() => handleFormatChange(option.value)}
                    xcss={[formatButtonStyles, isSelected && selectedFormatButtonStyles]}
                  >
                    <Stack alignInline="center" space="space.025">
                      <Text color="inherit" size="large">
                        {option.icon}
                      </Text>
                      <Text color="inherit">{option.label}</Text>
                    </Stack>
                  </Pressable>
                );
              })}
            </Inline>
          )}
        </Field>

        <Field
          label={
            <Text size="small" weight="bold" color="color.text.subtle">
              {decimalPlacesLabel}
            </Text>
          }
          name="number-decimal-places-field"
        >
          {({ fieldProps }) => (
            <Box testId="number-decimal-places-picker-wrapper" xcss={inputWrapperStyles}>
              <Select<DecimalPlacesOption>
                ariaLabel={decimalPlacesLabel}
                classNamePrefix="decimal-places-picker"
                formatOptionLabel={formatDecimalPlacesOption}
                inputId={fieldProps.id}
                isClearable={false}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                onChange={(option) =>
                  handleDecimalPlacesChange(option as DecimalPlacesOption)
                }
                options={decimalPlacesOptions}
                spacing="compact"
                styles={decimalPlacesSelectStyles}
                value={
                  decimalPlacesOptions.find(
                    (option) => option.value === field.settings.numberFormat.decimalPlaces
                  ) ?? decimalPlacesOptions[0]
                }
              />
            </Box>
          )}
        </Field>

        {field.settings.numberFormat.format === NumberFormats.CURRENCY && (
          <Field
            label={
              <Text size="small" weight="bold" color="color.text.subtle">
                {currencyLabel}
              </Text>
            }
            name="number-currency-field"
          >
            {({ fieldProps }) => (
              <Select<CurrencyOption>
                ariaLabel={currencyLabel}
                classNamePrefix="currency-type-picker"
                inputId={fieldProps.id}
                isClearable={false}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                onChange={(option) => handleCurrencyChange(option as CurrencyOption)}
                options={currencyOptions}
                spacing="compact"
                styles={currencySelectStyles}
                value={currencyOptions.find(
                  (option) => option.value === field.settings.numberFormat.prefix
                )}
              />
            )}
          </Field>
        )}

        <Field
          label={
            <Text size="small" weight="bold" color="color.text.subtle">
              {separatorsLabel}
            </Text>
          }
          name="number-separators-field"
        >
          {({ fieldProps }) => (
            <Box testId="number-separator-picker-wrapper" xcss={inputWrapperStyles}>
              <Select<SeparatorOption>
                ariaLabel={separatorsLabel}
                classNamePrefix="number-separator-picker"
                formatOptionLabel={formatSeparatorOption}
                inputId={fieldProps.id}
                isClearable={false}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                onChange={(option) => handleSeparatorChange(option as SeparatorOption)}
                options={separatorOptions}
                spacing="compact"
                styles={separatorSelectStyles}
                value={separatorOptions.find((option) =>
                  isSelectedSeparator(option, field.settings.numberFormat)
                )}
              />
            </Box>
          )}
        </Field>
      </Stack>
    </Box>
  );
};

export default NumberFieldSettings;
